import * as THREE from 'three';
import {
  BOWSPRIT, CHANNELS, MASTS, SAILS, YARDS, bowspritPoint, deckHalfWidth, deckY, halfBreadthXY, mast, mastXAt, sailMount, sheerY, yard,
  type ForeAftSailSpec, type MastId, type MastSpec, type SquareSailSpec, type YardSpec,
} from '../../data/ship';
import type { SailRig, SailState } from '../../sim/sails';
import type { DamageModel } from '../../sim/damage';
import { box, cachedBatch, lathe, rod, slab, V, type Batch } from './geom';
import { PartSet, RopeSet } from './ropes';
import type { ShipMaterials } from '../materials/materials';
import { patchMaterial } from '../atmosphere';
import { instantiate, type ArtSet } from './art';

// Masts, yards, standing and running rigging, and the sails. Each mast is
// built in three pieces (lower mast, topmast, topgallant) so shot can carry
// them away one at a time; standing rigging is rebuilt when the damage model
// changes, leaving out what has gone by the board or been shot through.

const SLING = 0.55;

/** Everything aloft fits in this (design space): used for culling cloth and rope. */
const RIG_SPHERE = new THREE.Sphere(new THREE.Vector3(4, 18, 0), 36);

const SHROUDS: Record<string, number> = { fore: 4, main: 5, mizzen: 3 };

interface SailMesh {
  mesh: THREE.Mesh;
  pos: THREE.BufferAttribute;
  nrm: THREE.BufferAttribute;
  version: number;
}

interface GaffRig {
  sail: SailState;
  spec: ForeAftSailSpec;
  gaff: THREE.Group;
  boom: THREE.Group;
}

export interface RigClass {
  masts: readonly MastId[];
  sails: readonly string[];
}

/** Hash for picking which ropes a damaged rig has lost (stable per rope). */
function ropeHash(k: number): number {
  const x = Math.sin(k * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}

export class Rig {
  readonly group = new THREE.Group();
  private readonly parts = new Map<string, THREE.Object3D>();
  private readonly stumps = new Map<MastId, THREE.Mesh>();
  private readonly yardGroups = new Map<string, THREE.Group>();
  private readonly gaffs: GaffRig[] = [];
  readonly ropes: RopeSet;
  readonly blocks: PartSet;
  readonly deadeyes: PartSet;
  /**
   * Every sail's cloth in one mesh: one draw a pass for the whole suit
   * instead of one per sail. Each sail's vertices start at sailBase[i]; the
   * index lists the sails being shown (set, not lost, holes left out) and
   * is rebuilt only when that changes.
   */
  private sailMesh!: THREE.Mesh;
  private sailPos!: THREE.BufferAttribute;
  private sailNrm!: THREE.BufferAttribute;
  private readonly sailBase: number[] = [];
  private sailShown = '-';
  /** Furled canvas lashed along each yard / stay, one per sail. */
  private readonly bundles: THREE.Mesh[] = [];
  private readonly flagMeshes: SailMesh[] = [];
  private readonly has: Set<MastId>;
  private readonly topgallants: Set<MastId>;
  private signature = '';
  private ropeId = 0;
  /** Draw running rigging and rope detail (off for distant ships). */
  detail = true;
  /** Far off: spars and canvas only, no rigging at all. */
  private low = false;
  private standingDirty = false;
  private uploaded = -1;
  /** Bumped when new pieces (stumps) are added, so the model can restyle them. */
  pieces = 0;

  /** `art`: the Blender-built masts and spars (null: build them procedurally). */
  constructor(
    private readonly mats: ShipMaterials, private readonly rigSim: SailRig, cls: RigClass, private readonly damage: DamageModel | null,
    private readonly art: ArtSet | null = null,
  ) {
    this.has = new Set(cls.masts);
    // Topgallant masts only where the class sets topgallant sails.
    this.topgallants = new Set(cls.masts.filter((m) => cls.sails.some((id) => sailMount(id).mast === m && sailMount(id).level === 2)));
    for (const m of MASTS) {
      if (!this.has.has(m.id)) continue;
      for (let level = 0; level < 3; level++) {
        if (level === 2 && !this.topgallants.has(m.id)) continue;
        const tg = this.topgallants.has(m.id);
        const g = this.piece(level === 1 && !tg ? `mast_${m.id}_1_truck` : `mast_${m.id}_${level}`, false)
          ?? cachedBatch(`mast:${m.id}:${level}:${tg ? 1 : 0}`, (b) => buildMastLevel(b, m, level, tg), mats);
        this.parts.set(`${m.id}:${level}`, g);
        this.group.add(g);
      }
    }
    this.group.add(this.piece('bowsprit', false) ?? cachedBatch('bowsprit', (b) => buildBowsprit(b), mats));

    const yards = new Set<string>();
    for (const s of rigSim.sails) if (s.spec.kind === 'square') yards.add(s.spec.yard);
    for (const y of YARDS) {
      if (!yards.has(y.id)) continue;
      const g = new THREE.Group();
      g.add(this.piece(`yard_${y.id}`, true) ?? cachedBatch(`yard:${y.id}`, (b) => buildYard(b, y), mats));
      g.position.set(mastXAt(mast(y.mast), y.y), y.y, 0);
      this.yardGroups.set(y.id, g);
      this.group.add(g);
    }
    for (const s of rigSim.sails) {
      if (s.spec.kind === 'foreaft' && s.spec.peak) this.buildGaffBoom(s, s.spec);
    }

    this.ropes = new RopeSet(mats.ropeTarred, 6000);
    const blockGeo = new THREE.SphereGeometry(1, 8, 6);
    blockGeo.scale(0.09, 0.15, 0.07);
    this.blocks = new PartSet(blockGeo, mats.timber, 400);
    const deGeo = new THREE.CylinderGeometry(0.16, 0.16, 0.09, 12);
    deGeo.rotateX(Math.PI / 2);
    this.deadeyes = new PartSet(deGeo, mats.blackWood, 200);
    this.rebuildStanding();
    for (const m of [this.ropes.mesh, this.blocks.mesh, this.deadeyes.mesh]) {
      m.boundingSphere = RIG_SPHERE.clone();
      m.frustumCulled = true;
    }
    this.group.add(this.ropes.mesh, this.blocks.mesh, this.deadeyes.mesh);

    this.buildSails();
  }

  /** A piece of the Blender rig, if there is one. `local`: it's positioned by its parent. */
  private piece(name: string, local: boolean): THREE.Object3D | null {
    const node = this.art?.node(name);
    return node ? instantiate(node, this.mats, local) : null;
  }

  /** Far off: drop the rigging (the model swaps the materials). */
  setLow(low: boolean): void {
    if (this.low === low) return;
    this.low = low;
    if (!low && this.standingDirty) {
      this.standingDirty = false;
      this.rebuildStanding();
    }
  }

  /** Is this piece of this mast still standing? */
  private standing(id: MastId, level: number): boolean {
    if (!this.has.has(id)) return false;
    if (level === 2 && !this.topgallants.has(id)) return false;
    const lost = this.damage?.masts[id]?.lostLevel ?? 0;
    return level < 3 - lost;
  }

  /** Stable per-rope choice of what a shot-up rig has lost. */
  private keep(id: MastId): boolean {
    const k = this.ropeId++;
    const r = this.damage?.masts[id]?.rigging ?? 1;
    return r >= 0.999 || ropeHash(k + (id === 'fore' ? 0 : id === 'main' ? 5000 : 10000)) < 0.15 + r * 0.85;
  }

  private buildGaffBoom(sail: SailState, spec: ForeAftSailSpec): void {
    const peak = spec.peak!;
    const throat = V(spec.head.x, spec.head.y, 0);
    const boomP = V(spec.tack.x, spec.tack.y - 0.3, 0);
    const gaff = new THREE.Group();
    gaff.add(this.piece(`gaff_${spec.id}`, true) ?? cachedBatch(`gaff:${spec.id}`, (b) => b.add('spar', rod(V(0.25, -0.05, 0), V(peak.x - throat.x, peak.y - throat.y, 0), 0.12, 0.08, 10)), this.mats));
    gaff.position.copy(throat);
    const boom = new THREE.Group();
    boom.add(this.piece(`boom_${spec.id}`, true) ?? cachedBatch(`boom:${spec.id}`, (b) => b.add('spar', rod(V(0.25, -0.05, 0), V(spec.clew.x - boomP.x - 0.4, spec.clew.y - boomP.y - 0.05, 0), 0.14, 0.1, 10)), this.mats));
    boom.position.copy(boomP);
    this.group.add(gaff, boom);
    this.gaffs.push({ sail, spec, gaff, boom });
  }

  // ------------------------------------------------------------- rigging

  private rebuildStanding(): void {
    const R = this.ropes;
    R.reset();
    this.blocks.reset();
    this.deadeyes.reset();
    this.ropeId = 0;
    const up = new THREE.Quaternion();
    const add = (id: MastId, level: number, a: THREE.Vector3, b: THREE.Vector3, r: number) => {
      if (this.standing(id, level) && this.keep(id)) R.add(a, b, r);
    };
    for (const m of MASTS) {
      if (!this.has.has(m.id)) continue;
      const [cx, clen] = CHANNELS[m.id];
      const n = SHROUDS[m.id]!;
      const chY = sheerY(cx) - 0.55;
      const headY = m.lowerTop - 1.3;
      for (const side of [1, -1] as const) {
        const shrouds: [THREE.Vector3, THREE.Vector3][] = [];
        for (let i = 0; i < n; i++) {
          const x = cx - clen / 2 + 0.5 + (i / Math.max(1, n - 1)) * (clen - 1.2);
          const hb = halfBreadthXY(x, chY);
          const foot = V(x, chY + 0.28, side * (hb + 0.5));
          const upperDE = V(x, chY + 0.95, side * (hb + 0.48));
          const head = V(mastXAt(m, headY) - 0.05 * i, headY, side * 0.34);
          // Deadeyes and lanyards stay with the channel even if the mast goes.
          const dir = new THREE.Vector3().subVectors(head, upperDE).normalize();
          const q = up.setFromUnitVectors(new THREE.Vector3(0, 0, 1), new THREE.Vector3(side, 0, 0));
          this.deadeyes.add(foot, q);
          R.add(V(x, chY + 0.22, side * (hb + 0.5)), V(x - 0.1, chY - 1.4, side * (halfBreadthXY(x, chY - 1.4) + 0.04)), 0.02);
          if (!this.standing(m.id, 0)) continue;
          const keep = this.keep(m.id);
          if (keep) {
            this.deadeyes.add(upperDE, q);
            for (const dz of [-0.07, 0, 0.07]) R.add(V(foot.x + dz, foot.y + 0.05, foot.z), V(upperDE.x + dz, upperDE.y - 0.05, upperDE.z), 0.008);
            R.add(upperDE.clone().addScaledVector(dir, 0.1), head, 0.03);
          } else {
            // Shot through: the upper part hangs from the masthead.
            const hang = head.clone().addScaledVector(new THREE.Vector3().subVectors(upperDE, head), 0.35);
            hang.y -= 1.2;
            R.add(head, hang, 0.03);
          }
          shrouds.push([upperDE, head]);
        }
        // Ratlines between adjacent shrouds.
        for (let i = 0; i < shrouds.length - 1; i++) {
          const [a0, a1] = shrouds[i]!;
          const [b0, b1] = shrouds[i + 1]!;
          for (let y = a0.y + 0.6; y < headY - 1.6; y += 0.38) {
            const ta = (y - a0.y) / (a1.y - a0.y);
            const tb = (y - b0.y) / (b1.y - b0.y);
            add(m.id, 0, new THREE.Vector3().lerpVectors(a0, a1, ta), new THREE.Vector3().lerpVectors(b0, b1, tb), 0.009);
          }
        }
        // Topmast shrouds from the rim of the top, with futtock shrouds.
        const topY = m.lowerTop - 1.05;
        const tw = (m.id === 'main' ? 4.2 : m.id === 'fore' ? 3.8 : 3.0) / 2 - 0.15;
        const tmHead = m.topmastTop - 1.3;
        const tsh: [THREE.Vector3, THREE.Vector3][] = [];
        const nt = m.id === 'mizzen' ? 2 : 3;
        for (let i = 0; i < nt; i++) {
          if (!this.standing(m.id, 1)) break;
          const x = mastXAt(m, topY) - 0.5 + i * 0.45;
          const foot = V(x, topY + 0.15, side * tw);
          const head = V(mastXAt(m, tmHead) + 0.5, tmHead, side * 0.2);
          add(m.id, 1, foot, head, 0.022);
          add(m.id, 1, foot, V(mastXAt(m, topY - 2.6), topY - 2.6, side * (m.radius + 0.05)), 0.018);
          tsh.push([foot, head]);
          this.deadeyes.add(foot, up.setFromUnitVectors(new THREE.Vector3(0, 0, 1), new THREE.Vector3(side, 0, 0)), 0.6);
        }
        for (let i = 0; i < tsh.length - 1; i++) {
          const [a0, a1] = tsh[i]!;
          const [b0, b1] = tsh[i + 1]!;
          for (let y = a0.y + 0.45; y < tmHead - 1.2; y += 0.38) {
            add(m.id, 1, new THREE.Vector3().lerpVectors(a0, a1, (y - a0.y) / (a1.y - a0.y)), new THREE.Vector3().lerpVectors(b0, b1, (y - b0.y) / (b1.y - b0.y)), 0.008);
          }
        }
        // Topgallant shrouds from the crosstrees.
        if (m.topgallantTop) {
          const ctY = m.topmastTop - 0.9;
          const tgHead = m.topgallantTop - 0.9;
          for (const dx of [-0.2, 0.3]) add(m.id, 2, V(mastXAt(m, ctY) + 0.5 + dx, ctY, side * 1.05), V(mastXAt(m, tgHead) + 0.85, tgHead, side * 0.12), 0.015);
        }
        // Backstays to the after end of the channels.
        const bx = cx - clen / 2 - 0.2;
        const bhb = halfBreadthXY(bx, chY);
        const bfoot = V(bx, chY + 0.28, side * (bhb + 0.5));
        this.deadeyes.add(bfoot, up.setFromUnitVectors(new THREE.Vector3(0, 0, 1), new THREE.Vector3(side, 0, 0)));
        add(m.id, 1, bfoot, V(mastXAt(m, tmHead) + 0.5, tmHead + 0.2, side * 0.22), 0.024);
        if (m.topgallantTop) {
          const bx2 = bx - 0.5;
          const f2 = V(bx2, chY + 0.28, side * (halfBreadthXY(bx2, chY) + 0.5));
          add(m.id, 2, f2, V(mastXAt(m, m.topgallantTop - 0.9) + 0.85, m.topgallantTop - 0.9, side * 0.1), 0.017);
        }
      }
    }

    // Stays, fore and aft: each belongs to the mast at its upper end and
    // needs the mast it leads down to still standing.
    const bsp = (s: number) => {
      const q = bowspritPoint(s);
      return V(q.x, q.y, 0);
    };
    const fore = mast('fore'), main = mast('main'), miz = mast('mizzen');
    const head = (m: MastSpec, y: number, dx = 0) => V(mastXAt(m, y) + dx, y, 0);
    const stay = (id: MastId, level: number, a: THREE.Vector3, b: THREE.Vector3, r: number, to?: [MastId, number]) => {
      if (to && !this.standing(to[0], to[1])) return;
      add(id, level, a, b, r);
    };
    stay('fore', 0, head(fore, fore.lowerTop - 1.3), bsp(2.4), 0.06);
    stay('fore', 0, head(fore, 17.2), bsp(3.4), 0.03);
    stay('fore', 1, head(fore, 26.2, 0.5), bsp(11.2), 0.04);
    stay('fore', 2, head(fore, 33.6, 0.85), bsp(16.8), 0.025);
    stay('fore', 2, head(fore, fore.topgallantTop! - 0.3, 0.85), bsp(18.8), 0.02);
    stay('main', 0, head(main, main.lowerTop - 1.3), V(mastXAt(fore, deckY(12.8) + 1.0) - 0.4, deckY(12.8) + 1.0, 0), 0.065);
    stay('main', 1, head(main, main.topmastTop - 1.3, 0.5), head(fore, fore.lowerTop - 0.8, 0.2), 0.04, ['fore', 0]);
    stay('main', 2, head(main, main.topgallantTop! - 0.9, 0.85), head(fore, fore.topmastTop - 0.8, 0.5), 0.025, ['fore', 1]);
    stay('mizzen', 0, head(miz, miz.lowerTop - 1.3), head(main, 5.8, -0.4), 0.045, ['main', 0]);
    stay('mizzen', 1, head(miz, miz.topmastTop - 1.3, 0.5), head(main, main.lowerTop - 0.9), 0.03, ['main', 0]);
    // Bobstays, bowsprit shrouds and martingale.
    const striker = bsp(BOWSPRIT.length - 0.3);
    const strikerFoot = V(striker.x + 0.2, striker.y - 2.3, 0);
    R.add(bsp(BOWSPRIT.length - 0.6), V(21.4, 1.9, 0), 0.05);
    R.add(bsp(BOWSPRIT.length - 2.6), V(21.0, 1.3, 0), 0.05);
    for (const side of [1, -1]) R.add(bsp(8.5), V(18.8, 2.8, side * 1.9), 0.035);
    R.add(bsp(BOWSPRIT.jibboomStart + BOWSPRIT.jibboomLength - 0.2), strikerFoot, 0.022);
    for (const side of [1, -1]) R.add(strikerFoot, V(19.2, 2.2, side * 1.2), 0.022);
    for (const side of [1, -1]) R.sagging(V(bsp(9).x, bsp(9).y - 0.2, side * 0.25), V(bsp(18).x, bsp(18).y - 0.1, 0), 0.012, 0.8, 6);

    // Lead blocks along the rails.
    for (const x of [4, -11.5, 1.2]) for (const side of [1, -1]) this.blocks.add(V(x, deckY(x) + 1.05, side * (deckHalfWidth(x) - 0.1)), new THREE.Quaternion());
    R.lock();
    this.blocks.lock();
    this.deadeyes.lock();
  }

  // ------------------------------------------------------------- sails

  private buildSails(): void {
    const sails = this.rigSim.sails;
    let total = 0;
    for (const s of sails) {
      this.sailBase.push(total);
      total += s.cloth.count;
    }
    const pos = new Float32Array(total * 3), nrm = new Float32Array(total * 3), uv = new Float32Array(total * 2);
    sails.forEach((s, k) => {
      const c = s.cloth, base = this.sailBase[k]!;
      pos.set(c.pos, base * 3);
      nrm.set(c.normal, base * 3);
      // UVs in metres from the rest shape.
      const w = sailWidth(s.spec), h = sailHeight(s.spec);
      for (let i = 0; i < c.count; i++) {
        uv[(base + i) * 2] = c.uv[i * 2]! * w;
        uv[(base + i) * 2 + 1] = (1 - c.uv[i * 2 + 1]!) * h;
      }
      this.bundles.push(this.makeBundle(s.spec));
    });
    const g = new THREE.BufferGeometry();
    this.sailPos = new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage);
    this.sailNrm = new THREE.BufferAttribute(nrm, 3).setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('position', this.sailPos);
    g.setAttribute('normal', this.sailNrm);
    g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    g.setIndex(new THREE.BufferAttribute(new Uint32Array(0), 1));
    g.boundingSphere = RIG_SPHERE.clone();
    const mesh = new THREE.Mesh(g, this.mats.canvas);
    mesh.name = 'canvas';
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.group.add(mesh);
    this.sailMesh = mesh;
    const pennantMat = patchMaterial(new THREE.MeshStandardMaterial({ color: new THREE.Color(0.35, 0.03, 0.02), side: THREE.DoubleSide, roughness: 0.9 }), undefined, 'pennant');
    this.rigSim.flags.forEach((f, i) => {
      const c = f.cloth;
      const g = new THREE.BufferGeometry();
      const pos = new THREE.BufferAttribute(c.pos, 3);
      const nrm = new THREE.BufferAttribute(c.normal, 3);
      g.setAttribute('position', pos);
      g.setAttribute('normal', nrm);
      const uv = new Float32Array(c.count * 2);
      for (let k = 0; k < c.count; k++) {
        uv[k * 2] = c.uv[k * 2]!;
        uv[k * 2 + 1] = 1 - c.uv[k * 2 + 1]!;
      }
      g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
      g.setIndex(new THREE.BufferAttribute(c.indices, 1));
      g.boundingSphere = RIG_SPHERE.clone();
      const mesh = new THREE.Mesh(g, i === 0 ? this.mats.flag : pennantMat);
      mesh.name = i === 0 ? 'flag' : 'pennant';
      mesh.castShadow = true;
      this.group.add(mesh);
      this.flagMeshes.push({ mesh, pos, nrm, version: 0 });
    });
  }

  private makeBundle(spec: SquareSailSpec | ForeAftSailSpec): THREE.Mesh {
    let geo: THREE.BufferGeometry;
    let parent: THREE.Object3D = this.group;
    if (spec.kind === 'square') {
      const y = yard(spec.yard);
      // Canvas gathered on top of the yard, fattest in the bunt.
      const prof: [number, number][] = [];
      for (let i = 0; i <= 12; i++) {
        const t = i / 12;
        prof.push([0.08 + 0.22 * Math.sin(t * Math.PI) ** 0.6, (t * 2 - 1) * y.half * 0.92]);
      }
      geo = lathe(prof, 10);
      geo.rotateX(Math.PI / 2);
      geo.translate(SLING + 0.1, y.radius + 0.12, 0);
      parent = this.yardGroups.get(spec.yard)!;
    } else if (spec.peak) {
      const a = V(spec.tack.x, spec.tack.y + 0.5, 0), b = V(spec.head.x, spec.head.y - 0.4, 0);
      geo = rod(a, b, 0.22, 0.14, 10);
    } else {
      const a = V(spec.tack.x, spec.tack.y, 0);
      const b = new THREE.Vector3(spec.head.x, spec.head.y, 0).sub(a).multiplyScalar(0.18).add(a);
      geo = rod(a, b, 0.2, 0.12, 10);
    }
    const m = new THREE.Mesh(geo, this.mats.canvas);
    m.name = 'canvas';
    m.castShadow = true;
    m.receiveShadow = true;
    m.visible = false;
    parent.add(m);
    return m;
  }

  /** Stump of a lower mast carried away, splintered at the break. */
  private stump(m: MastSpec, top: number): THREE.Mesh {
    const heel = m.deckY - 1.2;
    const pts: [number, number][] = [[0.001, 0], [m.radius, 0], [m.radius * 0.97, top - heel - 0.4]];
    // Jagged top: a few splinters standing up out of the break.
    for (let i = 0; i < 5; i++) pts.push([m.radius * (0.85 - i * 0.16), top - heel - 0.3 + (i % 2 === 0 ? 0.5 : -0.1)]);
    pts.push([0.001, top - heel - 0.2]);
    const g = lathe(pts, 12);
    const mesh = new THREE.Mesh(g, this.mats.spar);
    mesh.name = 'spar';
    this.pieces++;
    mesh.position.set(mastXAt(m, heel), heel, 0);
    mesh.rotation.z = m.rake;
    mesh.castShadow = mesh.receiveShadow = true;
    this.group.add(mesh);
    return mesh;
  }

  // ------------------------------------------------------------- per frame

  update(struck = false): void {
    const rig = this.rigSim;
    const dmg = this.damage;
    // Rebuild what stands when the mast damage changes.
    if (dmg) {
      let sig = '';
      for (const id of this.has) {
        const md = dmg.masts[id];
        sig += `${md?.lostLevel ?? 0}${Math.round((md?.rigging ?? 1) * 8)}`;
      }
      if (sig !== this.signature) {
        this.signature = sig;
        for (const [key, g] of this.parts) {
          const [id, level] = key.split(':') as [MastId, string];
          g.visible = this.standing(id, Number(level));
        }
        for (const id of this.has) {
          const md = dmg.masts[id];
          if (md && md.lostLevel >= 3 && !this.stumps.has(id)) this.stumps.set(id, this.stump(mast(id), md.stump));
          // Restepped (the dev tools' repair): the stump goes.
          const stump = this.stumps.get(id);
          if (stump && (!md || md.lostLevel < 3)) {
            this.group.remove(stump);
            stump.geometry.dispose();
            this.stumps.delete(id);
          }
        }
        if (this.low) this.standingDirty = true;
        else this.rebuildStanding();
      }
    }
    for (const [id, g] of this.yardGroups) {
      const y = yard(id);
      g.visible = this.standing(y.mast, yardLevel(id));
      g.rotation.y = rig.brace[y.mast];
      g.updateMatrix();
    }
    for (const gr of this.gaffs) {
      const mount = sailMount(gr.spec.id);
      const up = this.standing(mount.mast, 0);
      gr.gaff.visible = gr.boom.visible = up;
      const sheet = rig.sheet[gr.spec.id] ?? 0;
      gr.gaff.rotation.y = sheet;
      gr.boom.rotation.y = sheet;
      gr.gaff.updateMatrix();
      gr.boom.updateMatrix();
    }

    // Furled sails are drawn as lashed bundles rather than loose cloth; lost
    // and shot-through canvas is gone or holed. Cloth is only re-uploaded
    // when the sim has moved it (distant ships step less often).
    const fresh = rig.updates !== this.uploaded;
    this.uploaded = rig.updates;
    let shown = '';
    rig.sails.forEach((s, i) => {
      const set = s.deploy >= 0.05 && !s.lost;
      if (set) {
        shown += `${i}.${s.cloth.version} `;
        if (fresh) {
          (this.sailPos.array as Float32Array).set(s.cloth.pos, this.sailBase[i]! * 3);
          (this.sailNrm.array as Float32Array).set(s.cloth.normal, this.sailBase[i]! * 3);
        }
      }
      const b = this.bundles[i]!;
      b.visible = s.deploy < 0.25 && !s.lost;
      const k = Math.max(0.05, 1 - s.deploy / 0.25);
      if (s.spec.kind === 'square') b.scale.set(k, k, 1);
      else b.scale.setScalar(Math.max(0.3, k));
    });
    if (fresh) this.sailPos.needsUpdate = this.sailNrm.needsUpdate = true;
    if (shown !== this.sailShown) {
      // Another sail set, taken in, lost or holed: which triangles to draw.
      this.sailShown = shown;
      const parts: Uint32Array[] = [];
      let n = 0;
      rig.sails.forEach((s, i) => {
        if (s.deploy < 0.05 || s.lost) return;
        const idx = s.cloth.version > 0 ? s.cloth.aliveIndices() : s.cloth.indices;
        parts.push(idx.map((v) => v + this.sailBase[i]!));
        n += idx.length;
        // A sail just set may not have been copied since it was last shown.
        (this.sailPos.array as Float32Array).set(s.cloth.pos, this.sailBase[i]! * 3);
        (this.sailNrm.array as Float32Array).set(s.cloth.normal, this.sailBase[i]! * 3);
      });
      this.sailPos.needsUpdate = this.sailNrm.needsUpdate = true;
      const all = new Uint32Array(n);
      let o = 0;
      for (const p of parts) (all.set(p, o), (o += p.length));
      this.sailMesh.geometry.setIndex(new THREE.BufferAttribute(all, 1));
      this.sailMesh.visible = n > 0;
    }
    rig.flags.forEach((f, i) => {
      const fm = this.flagMeshes[i]!;
      fm.mesh.visible = !f.lost && !(struck && i === 0);
      if (fresh) {
        fm.pos.needsUpdate = true;
        fm.nrm.needsUpdate = true;
      }
    });

    if (this.low) return;
    const R = this.ropes;
    R.beginFrame();
    this.blocks.beginFrame();
    if (this.detail) this.runningRigging();
    R.endFrame();
    this.blocks.endFrame();
  }

  private runningRigging(): void {
    const rig = this.rigSim;
    const R = this.ropes;
    const tmp = new THREE.Vector3();
    const inYard = (id: string, x: number, y: number, z: number) => new THREE.Vector3(x, y, z).applyMatrix4(this.yardGroups.get(id)!.matrix);

    for (const [id, g] of this.yardGroups) {
      if (!g.visible) continue;
      const y = yard(id);
      const m = mast(y.mast);
      const arms = [1, -1].map((s) => inYard(y.id, SLING - 0.05, 0.05, s * (y.half - 0.15)));
      // Footropes and stirrups.
      for (const s of [1, -1]) {
        const pts: THREE.Vector3[] = [];
        for (let i = 0; i <= 6; i++) {
          const t = i / 6;
          const z = s * (0.5 + t * (y.half - 0.7));
          pts.push(inYard(y.id, SLING - 0.25, -0.95 - Math.sin(t * Math.PI) * 0.18, z));
        }
        R.polyline(pts, 0.013);
        for (const t of [0.3, 0.7]) {
          const z = s * (0.5 + t * (y.half - 0.7));
          R.add(inYard(y.id, SLING - 0.05, -0.05, z), inYard(y.id, SLING - 0.25, -0.95 - Math.sin(t * Math.PI) * 0.18, z), 0.01);
        }
      }
      // Lifts to the masthead above.
      const liftTo = V(mastXAt(m, y.y + 2.2) + (y.y > m.lowerTop ? 0.5 : 0), y.y + 2.2, 0);
      for (const a of arms) R.add(a, liftTo, 0.014);
      R.add(inYard(y.id, SLING, 0.15, 0), V(mastXAt(m, y.y + 1.2) + 0.3, y.y + 1.2, 0), 0.03);
      // Braces.
      for (const [i, a] of arms.entries()) {
        const s = i === 0 ? 1 : -1;
        const lead = this.braceLead(y.id, s);
        R.add(a, lead, 0.016);
        this.blocks.addAlong(a, tmp.subVectors(lead, a), 1);
      }
    }

    // Sail edges: bolt ropes along foot and leeches; sheets and tacks.
    for (const s of rig.sails) {
      if (s.lost) continue;
      const c = s.cloth;
      const P = (i: number) => new THREE.Vector3(c.pos[i * 3]!, c.pos[i * 3 + 1]!, c.pos[i * 3 + 2]!);
      if (s.deploy > 0.06 && c.health > 0.6) {
        const foot: THREE.Vector3[] = [];
        for (let k = 0; k < c.cols; k++) foot.push(P((c.rows - 1) * c.cols + k));
        R.polyline(foot, 0.016);
        const l1: THREE.Vector3[] = [], l2: THREE.Vector3[] = [];
        for (let r = 0; r < c.rows; r++) {
          l1.push(P(r * c.cols));
          l2.push(P(r * c.cols + c.cols - 1));
        }
        R.polyline(l1, 0.014);
        R.polyline(l2, 0.014);
      }
      const spec = s.spec;
      if (spec.kind === 'square' && !spec.footYard && s.deploy > 0.2) {
        for (const k of [(c.rows - 1) * c.cols, c.rows * c.cols - 1]) {
          const clew = P(k);
          const sd = Math.sign(clew.z) || 1;
          const hw = deckHalfWidth(clew.x - 4) - 0.1;
          R.add(clew, V(clew.x - 4.5, deckY(clew.x - 4.5) + 1.1, sd * hw), 0.022);
          R.add(clew, V(clew.x + 3.6, deckY(clew.x + 3.6) + 1.1, sd * (deckHalfWidth(clew.x + 3.6) - 0.1)), 0.022);
          this.blocks.add(clew, new THREE.Quaternion(), 1.1);
        }
      } else if (spec.kind === 'foreaft' && !spec.peak && s.deploy > 0.1) {
        const clew = P(c.cols - 1);
        const sd = Math.sign(clew.z) || 1;
        const lx = Math.min(clew.x - 1.2, 17.5);
        R.add(clew, V(lx, deckY(lx) + 1.0, sd * (deckHalfWidth(lx) - 0.2)), 0.016);
        this.blocks.add(clew, new THREE.Quaternion(), 0.9);
      }
    }
    // Gaff sails: peak halyard, vangs and boom sheet.
    for (const gr of this.gaffs) {
      if (!gr.gaff.visible) continue;
      const spec = gr.spec;
      const m = mast(sailMount(spec.id).mast);
      const peak = new THREE.Vector3(spec.peak!.x - gr.gaff.position.x, spec.peak!.y - gr.gaff.position.y, 0).applyMatrix4(gr.gaff.matrix);
      const boomEnd = new THREE.Vector3(spec.clew.x - gr.boom.position.x - 0.4, spec.clew.y - gr.boom.position.y - 0.05, 0).applyMatrix4(gr.boom.matrix);
      R.add(peak, V(mastXAt(m, m.lowerTop - 0.5), m.lowerTop - 0.5, 0), 0.016);
      const vx = Math.max(-17.2, spec.clew.x - 1.5);
      for (const s of [1, -1]) R.add(peak, V(vx, sheerY(vx) + 0.2, s * (deckHalfWidth(vx) + 0.05)), 0.011);
      const sx = Math.max(-18.2, spec.clew.x - 0.8);
      R.add(boomEnd, V(sx, deckY(sx) + 0.9, 0), 0.02);
    }
  }

  /** Where each yard's braces lead (ship-local), round missing masts. */
  private braceLead(id: string, side: number): THREE.Vector3 {
    const main = mast('main'), miz = mast('mizzen');
    const deckLead = (x: number) => V(x, deckY(x) + 1.1, side * (deckHalfWidth(x) - 0.1));
    switch (id) {
      case 'fore_course': return deckLead(4.2);
      case 'fore_topsail': return this.standing('main', 0) ? V(mastXAt(main, main.lowerTop - 1), main.lowerTop - 1, side * 1.3) : deckLead(-1);
      case 'fore_topgallant': return this.standing('main', 1) ? V(mastXAt(main, main.topmastTop - 1) + 0.5, main.topmastTop - 1, side * 0.4) : deckLead(-2);
      case 'main_course': return deckLead(-11.5);
      case 'main_topsail': return this.standing('mizzen', 0) ? V(mastXAt(miz, miz.lowerTop - 1), miz.lowerTop - 1, side * 1.1) : deckLead(-13.5);
      case 'main_topgallant': return this.standing('mizzen', 1) ? V(mastXAt(miz, miz.topmastTop - 1) + 0.5, miz.topmastTop - 1, side * 0.3) : deckLead(-15);
      case 'crossjack': return this.standing('main', 0) ? V(mastXAt(main, 8), 8, side * 0.8) : deckLead(-4);
      default: return this.standing('main', 0) ? V(mastXAt(main, main.lowerTop - 1.2), main.lowerTop - 1.2, side * 1.2) : deckLead(-8);
    }
  }
}

function yardLevel(id: string): number {
  return id.includes('topgallant') ? 2 : id.includes('topsail') ? 1 : 0;
}

// ------------------------------------------------------------- geometry

function buildMastLevel(b: Batch, m: MastSpec, level: number, topgallant: boolean): void {
  const r = m.radius;
  const heel = m.deckY - 1.2;
  const P = (y: number, dx = 0) => V(mastXAt(m, y) + dx, y, 0);
  if (level === 0) {
    b.add('spar', rod(P(heel), P(m.lowerTop + 0.4), r, r * 0.8, 20));
    // Iron hoops along the lower mast.
    for (let y = m.deckY + 1.6; y < m.lowerTop - 3; y += 1.25) {
      const t = new THREE.TorusGeometry(r * 0.98 - (y - m.deckY) * r * 0.012, 0.022, 6, 24);
      t.rotateX(Math.PI / 2);
      t.translate(mastXAt(m, y), y, 0);
      b.add('iron', t);
    }
    // Partners where she passes through the deck.
    const partner = new THREE.CylinderGeometry(r + 0.18, r + 0.25, 0.25, 16);
    partner.translate(mastXAt(m, m.deckY), m.deckY + 0.2, 0);
    b.add('timber', partner);
    // The top: platform on trestle and cross trees.
    const topY = m.lowerTop - 1.05;
    const tw = m.id === 'main' ? 4.2 : m.id === 'fore' ? 3.8 : 3.0;
    const td = tw * 0.62;
    const shape: THREE.Vector2[] = [];
    for (let i = 0; i <= 16; i++) {
      const a = -Math.PI / 2 + (i / 16) * Math.PI;
      shape.push(new THREE.Vector2(Math.cos(a) * td * 0.55 + td * 0.1, Math.sin(a) * tw / 2));
    }
    shape.push(new THREE.Vector2(-td * 0.45, tw / 2), new THREE.Vector2(-td * 0.45, -tw / 2));
    const plat = slab(shape, 0.1);
    plat.rotateX(Math.PI / 2);
    plat.translate(mastXAt(m, topY), topY + 0.05, 0);
    b.add('blackWood', plat);
    for (const z of [-0.45, 0.45]) {
      const tt = box(td * 1.05, 0.22, 0.14);
      tt.translate(mastXAt(m, topY), topY - 0.12, z);
      b.add('timber', tt);
    }
    for (const dx of [-td * 0.3, 0.1, td * 0.4]) {
      const ct = box(0.14, 0.16, tw * 0.95);
      ct.translate(mastXAt(m, topY) + dx, topY - 0.3, 0);
      b.add('timber', ct);
    }
    for (let i = 0; i <= 6; i++) {
      const z = -tw / 2 + 0.1 + (i / 6) * (tw - 0.2);
      b.add('timber', rod(V(mastXAt(m, topY) - td * 0.42, topY + 0.1, z), V(mastXAt(m, topY) - td * 0.42, topY + 0.85, z), 0.025, 0.025, 6));
    }
    b.add('timber', rod(V(mastXAt(m, topY) - td * 0.42, topY + 0.85, -tw / 2 + 0.1), V(mastXAt(m, topY) - td * 0.42, topY + 0.85, tw / 2 - 0.1), 0.04, 0.04, 6));
    const cap = box(1.0, 0.32, 0.62);
    cap.translate(mastXAt(m, m.lowerTop) + 0.28, m.lowerTop + 0.2, 0);
    b.add('blackWood', cap);
  } else if (level === 1) {
    // Topmast, fidded before the lower masthead.
    const tmR = r * 0.62;
    b.add('spar', rod(P(m.lowerTop - 2.4, 0.5), P(m.topmastTop + 0.3, 0.5), tmR, tmR * 0.7, 16));
    const ctY = m.topmastTop - 0.9;
    for (const dx of [-0.35, 0.45]) {
      const c = box(0.12, 0.12, 2.3);
      c.translate(mastXAt(m, ctY) + 0.5 + dx, ctY, 0);
      b.add('timber', c);
    }
    for (const z of [-0.25, 0.25]) {
      const tr = box(1.3, 0.14, 0.1);
      tr.translate(mastXAt(m, ctY) + 0.5, ctY + 0.1, z);
      b.add('timber', tr);
    }
    if (!topgallant || !m.topgallantTop) {
      const truck = new THREE.CylinderGeometry(0.14, 0.12, 0.12, 12);
      truck.translate(mastXAt(m, m.topmastTop + 0.3) + 0.5, m.topmastTop + 0.36, 0);
      b.add('blackWood', truck);
    }
  } else if (m.topgallantTop) {
    const tgR = r * 0.36;
    b.add('spar', rod(P(m.topmastTop - 1.6, 0.85), P(m.topgallantTop, 0.85), tgR, tgR * 0.6, 12));
    const truck = new THREE.CylinderGeometry(0.16, 0.14, 0.14, 12);
    truck.translate(mastXAt(m, m.topgallantTop) + 0.85, m.topgallantTop + 0.07, 0);
    b.add('blackWood', truck);
    const cap2 = box(0.6, 0.2, 0.4);
    cap2.translate(mastXAt(m, m.topmastTop) + 0.68, m.topmastTop + 0.2, 0);
    b.add('blackWood', cap2);
  }
}

function buildBowsprit(b: Batch): void {
  const B = BOWSPRIT;
  const p = (s: number) => {
    const q = bowspritPoint(s);
    return V(q.x, q.y, 0);
  };
  b.add('spar', rod(p(-2), p(B.length), B.radius, B.radius * 0.72, 16));
  b.add('spar', rod(p(B.jibboomStart), p(B.jibboomStart + B.jibboomLength), B.jibboomRadius, B.jibboomRadius * 0.6, 12));
  const cap = box(0.5, 0.6, 0.45);
  cap.rotateZ(B.angle);
  const c = p(B.length - 0.1);
  cap.translate(c.x, c.y + 0.1, 0);
  b.add('blackWood', cap);
  // Dolphin striker below the cap.
  const top = p(B.length - 0.3);
  b.add('spar', rod(top, V(top.x + 0.2, top.y - 2.3, 0), 0.07, 0.06, 8));
  // Gammoning lashings binding the bowsprit to the knee.
  for (let i = 0; i < 5; i++) {
    const q = p(3.2 + i * 0.12);
    const t = new THREE.TorusGeometry(B.radius + 0.03, 0.03, 5, 16);
    t.rotateY(Math.PI / 2);
    t.rotateZ(B.angle);
    t.translate(q.x, q.y, 0);
    b.add('rope', t);
  }
}

function buildYard(b: Batch, y: YardSpec): void {
  const r = y.radius;
  b.add('spar', rod(V(SLING, 0, 0), V(SLING, 0, y.half), r, r * 0.5, 12));
  b.add('spar', rod(V(SLING, 0, 0), V(SLING, 0, -y.half), r, r * 0.5, 12));
  // Sling cleats and iron bands.
  for (const z of [-0.5, 0.5, -y.half * 0.55, y.half * 0.55]) {
    const t = new THREE.TorusGeometry(r * 0.95, 0.018, 5, 16);
    t.rotateY(Math.PI / 2);
    t.translate(SLING, 0, z);
    b.add('iron', t);
  }
}

/** A mast (from a height up) and its yards as one piece, for wreckage. */
export function buildFallenSpar(b: Batch, id: MastId, fromY: number, topgallant: boolean, sails: readonly string[]): void {
  const m = mast(id);
  const top = topgallant && m.topgallantTop ? m.topgallantTop : m.topmastTop + 0.3;
  const segs: [number, number, number, number][] = [
    [m.deckY - 1.2, m.lowerTop + 0.4, 0, m.radius],
    [m.lowerTop - 2.4, m.topmastTop + 0.3, 0.5, m.radius * 0.62],
  ];
  if (topgallant && m.topgallantTop) segs.push([m.topmastTop - 1.6, m.topgallantTop, 0.85, m.radius * 0.36]);
  for (const [y0, y1, dx, r] of segs) {
    if (y1 <= fromY) continue;
    const a = Math.max(y0, fromY);
    b.add('spar', rod(V(mastXAt(m, a) + dx, a, 0), V(mastXAt(m, y1) + dx, y1, 0), r, r * 0.8, 12));
  }
  for (const y of YARDS) {
    if (y.mast !== id || y.y < fromY || y.y > top) continue;
    if (!sails.some((s) => SAILS.find((sp) => sp.id === s && sp.kind === 'square' && sp.yard === y.id))) continue;
    const x = mastXAt(m, y.y) + SLING;
    b.add('spar', rod(V(x, y.y, -y.half), V(x, y.y, y.half), y.radius, y.radius * 0.7, 8));
  }
  // A jagged break at the foot.
  const r0 = fromY < m.lowerTop ? m.radius : m.radius * 0.62;
  const shards = new THREE.ConeGeometry(r0 * 0.9, 0.7, 7, 1, true);
  shards.rotateX(Math.PI);
  shards.translate(mastXAt(m, fromY), fromY - 0.2, 0);
  b.add('spar', shards);
}

function sailWidth(s: SquareSailSpec | ForeAftSailSpec): number {
  if (s.kind === 'square') return (yard(s.yard).half + s.footHalf);
  return Math.hypot(s.clew.x - s.tack.x, s.clew.y - s.tack.y);
}
function sailHeight(s: SquareSailSpec | ForeAftSailSpec): number {
  if (s.kind === 'square') return yard(s.yard).y - s.footY;
  return Math.hypot(s.head.x - s.tack.x, s.head.y - s.tack.y);
}

export { SAILS };
